import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import ResponsiveTable from "@/components/Usados/ResponsiveTable";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import ActionMenu from "@/components/Usados/ActionMenu";
import { Modal } from "@/components/Usados/modal";
import Label from "@/components/Usados/Label";
import Badge from "@/components/ui/badge/Badge";
import {
  getFromLocal,
  saveToLocal,
  OFFLINE_KEYS,
} from "@/utils/offlineStorage";
import {
  User,
  Mail,
  Briefcase,
  Building,
  Shield,
  CheckCircle,
  Clock,
  FileText,
  Users,
} from "lucide-react";
import { apiFetch } from "@/service/api";

export default function Blank() {
  const [tareas, setTareas] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTarea, setSelectedTarea] = useState<any>(null);
  const { user } = useAuth();

  const fetchTareas = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const response = await apiFetch(
        `empleados-proyectos/find-tareas/${user.id}`,
      );
      let data = [];
      if (response.ok) data = await response.json();
      else data = getFromLocal(OFFLINE_KEYS.TAREAS) || [];
      const pendingTasks = getFromLocal(OFFLINE_KEYS.PENDING_TASKS) || [];
      const mappedData = data
        .filter((t: any) => t != null)
        .map((t: any) => {
          const pendingChange = pendingTasks.find((p: any) => p.id === t.id);
          const estadoOriginal =
            t.estado_tarea === true ||
            t.estado_tarea === 1 ||
            t.estado_tarea === "1";
          const estadoFinal = pendingChange
            ? pendingChange.estado_tarea
            : estadoOriginal;
          return {
            ...t,
            estado_tarea: estadoFinal,
            is_pending_sync: !!pendingChange,
            usuario: t.usuario ?? {
              nombres: "",
              apellidos: "",
              correo_electronico: "",
            },
            proyecto: t.proyecto ?? { nombre: "", empresa: "" },
            rol_proyecto: t.rol_proyecto ?? { nombre: "Sin rol" },
            equipo_proyecto: t.equipo_proyecto ?? {},
          };
        });
      setTareas(mappedData);
    } catch (error) {
      console.error("Error fetching tareas:", error);
      const offlineData = getFromLocal(OFFLINE_KEYS.TAREAS) || [];
      const pendingTasks = getFromLocal(OFFLINE_KEYS.PENDING_TASKS) || [];
      const mappedData = offlineData
        .filter((t: any) => t != null)
        .map((t: any) => {
          const pendingChange = pendingTasks.find((p: any) => p.id === t.id);
          const estadoOriginal =
            t.estado_tarea === true ||
            t.estado_tarea === 1 ||
            t.estado_tarea === "1";
          const estadoFinal = pendingChange
            ? pendingChange.estado_tarea
            : estadoOriginal;
          return {
            ...t,
            estado_tarea: estadoFinal,
            is_pending_sync: !!pendingChange,
            usuario: t.usuario ?? {
              nombres: "",
              apellidos: "",
              correo_electronico: "",
            },
            proyecto: t.proyecto ?? { nombre: "", empresa: "" },
            rol_proyecto: t.rol_proyecto ?? { nombre: "Sin rol" },
            equipo_proyecto: t.equipo_proyecto ?? {},
          };
        });
      setTareas(mappedData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTareas();
  }, []);

  const toggleTareaEstado = async (tarea: any) => {
    const nuevoEstado = !tarea.estado_tarea;
    try {
      const response = await apiFetch(
        `empleados-proyectos/update/${tarea.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ estado_tarea: nuevoEstado }),
        },
      );
      if (response.ok) {
        fetchTareas();
        if (selectedTarea && selectedTarea.id === tarea.id)
          setSelectedTarea({ ...selectedTarea, estado_tarea: nuevoEstado });
      } else {
        throw new Error("Offline or Server Error");
      }
    } catch (error) {
      console.error("Error updating tarea status, saving locally:", error);
      const pendingTasks = getFromLocal(OFFLINE_KEYS.PENDING_TASKS) || [];
      pendingTasks.push({ id: tarea.id, estado_tarea: nuevoEstado });
      saveToLocal(OFFLINE_KEYS.PENDING_TASKS, pendingTasks);
      setTareas((prev) =>
        prev.map((t) =>
          t.id === tarea.id ? { ...t, estado_tarea: nuevoEstado } : t,
        ),
      );
      if (selectedTarea && selectedTarea.id === tarea.id)
        setSelectedTarea({ ...selectedTarea, estado_tarea: nuevoEstado });
      alert(
        "Cambio guardado localmente (modo offline). Se sincronizara al subir informacion.",
      );
    }
  };

  const columns: any = [
    {
      label: "Usuario",
      key: "usuario",
      render: (value: any) =>
        `${value?.nombres ?? ""} ${value?.apellidos ?? ""}`,
    },
    {
      label: "Proyecto",
      key: "proyecto",
      render: (value: any) => value?.nombre ?? "Sin proyecto",
    },
    {
      label: "Empresa",
      key: "proyecto",
      render: (value: any) =>
        typeof value?.empresa === "string"
          ? value?.empresa
          : value?.empresa?.nombre ?? "Sin empresa",
    },
    {
      label: "Rol Proyecto",
      key: "rol_proyecto",
      render: (value: any) => value?.nombre ?? "Sin rol",
    },
    {
      label: "Estado Tarea",
      key: "estado_tarea",
      render: (value: any, row: any) => (
        <div className="flex flex-col items-end gap-1">
          <Badge
            color={value ? "primary" : "warning"}
            variant="light"
            startIcon={
              value ? (
                <CheckCircle className="size-3" />
              ) : (
                <Clock className="size-3" />
              )
            }
          >
            {value ? "Completada" : "Pendiente"}
          </Badge>
          {row.is_pending_sync && (
            <span className="text- text-orange-500 font-medium animate-pulse">
              Pendiente de sincronizar
            </span>
          )}
        </div>
      ),
    },
    {
      label: "Acciones",
      key: "actions",
      render: (_: any, row: any) => {
        const actions = [
          {
            label: "Ver",
            onClick: () => {
              setSelectedTarea(row);
              setModalOpen(true);
            },
          },
        ];
        const isCompleted =
          row.estado_tarea === true ||
          row.estado_tarea === 1 ||
          row.estado_tarea === "1" ||
          row.estado_tarea === "true";
        const isOnline = navigator.onLine;
        if (!isCompleted && isOnline)
          actions.push({
            label: "Marcar como Completada",
            onClick: () => toggleTareaEstado(row),
          });
        return <ActionMenu actions={actions} />;
      },
    },
  ];

  return (
    <div>
      <PageBreadcrumb pageTitle="Tareas del proyecto" />
      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <h3 className="mb-4 font-semibold text-gray-800 text-center text-theme-xl dark:text-white/90 sm:text-2xl">
          Tareas - Gestion
        </h3>
        <div className="w-full">
          {loading ? (
            <div className="flex items-center justify-center p-10">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-brand-500 rounded-full animate-spin"></div>
            </div>
          ) : (
            <ResponsiveTable columns={columns} data={tareas} />
          )}
        </div>
      </div>
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="max-w-3xl p-0 overflow-hidden"
        showCloseButton={false}
      >
        {selectedTarea && (
          <div className="flex flex-col h-full max-h-">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-white/[0.02]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white/90">
                    Detalle de la Tarea
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Informacion detallada de la asignacion
                  </p>
                </div>
                <Badge
                  color={selectedTarea.estado_tarea ? "success" : "warning"}
                  variant="light"
                  startIcon={
                    selectedTarea.estado_tarea ? (
                      <CheckCircle className="size-3" />
                    ) : (
                      <Clock className="size-3" />
                    )
                  }
                >
                  {selectedTarea.estado_tarea ? "Completada" : "Pendiente"}
                </Badge>
              </div>
            </div>
            <div className="px-6 py-6 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="space-y-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2">
                    <User className="size-3" /> Informacion del Usuario
                  </h4>
                  <div className="bg-gray-50 dark:bg-white/[0.03] p-4 rounded-xl border border-gray-100 dark:border-gray-800 space-y-3">
                    <div>
                      <Label className="text-xs text-gray-400 mb-0.5">
                        Nombre Completo
                      </Label>
                      <p className="text-sm font-medium text-gray-800 dark:text-white/90 flex items-center gap-2">
                        {selectedTarea.usuario?.nombres}{" "}
                        {selectedTarea.usuario?.apellidos}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 mb-0.5">
                        Correo Electronico
                      </Label>
                      <p className="text-sm font-medium text-gray-800 dark:text-white/90 flex items-center gap-2">
                        <Mail className="size-3.5 text-gray-400" />
                        {selectedTarea.usuario?.correo_electronico}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2">
                    <Briefcase className="size-3" /> Proyecto y Empresa
                  </h4>
                  <div className="bg-gray-50 dark:bg-white/[0.03] p-4 rounded-xl border border-gray-100 dark:border-gray-800 space-y-3">
                    <div>
                      <Label className="text-xs text-gray-400 mb-0.5">
                        Proyecto
                      </Label>
                      <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                        {selectedTarea.proyecto?.nombre}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 mb-0.5">
                        Empresa
                      </Label>
                      <p className="text-sm font-medium text-gray-800 dark:text-white/90 flex items-center gap-2">
                        <Building className="size-3.5 text-gray-400" />
                        {typeof selectedTarea.proyecto?.empresa === "string"
                          ? selectedTarea.proyecto?.empresa
                          : selectedTarea.proyecto?.empresa?.nombre}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2 space-y-4 mt-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2">
                    <Shield className="size-3" /> Rol y Responsabilidades
                  </h4>
                  <div className="bg-gray-50 dark:bg-white/[0.03] p-4 rounded-xl border border-gray-100 dark:border-gray-800 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-400 mb-0.5">
                        Rol en Proyecto
                      </Label>
                      <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                        {selectedTarea.rol_proyecto?.nombre ||
                          "Sin rol asignado"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 mb-0.5">
                        Equipo
                      </Label>
                      <p className="text-sm font-medium text-gray-800 dark:text-white/90 flex items-center gap-2">
                        <Users className="size-3.5 text-gray-400" />
                        {selectedTarea.equipo_proyecto?.nombre || "No asignado"}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs text-gray-400 mb-0.5">
                        <FileText className="size-3.5 inline mr-2 text-gray-400" />{" "}
                        Descripcion de la Tarea
                      </Label>
                      <div className="mt-1 p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800">
                        <p className="text-sm text-gray-600 dark:text-gray-400 italic whitespace-pre-wrap">
                          {selectedTarea.tarea
                            ? selectedTarea.tarea.replace(
                                /(\d+[.)]\s*)/g,
                                (match: string, _: string, offset: number) => {
                                  return offset === 0 ? match : `\n${match}`;
                                },
                              )
                            : "Sin descripcion detallada"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-white/[0.02] flex items-center justify-between gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                Cerrar
              </button>
              {!(
                selectedTarea.estado_tarea === true ||
                selectedTarea.estado_tarea === 1 ||
                selectedTarea.estado_tarea === "1" ||
                selectedTarea.estado_tarea === "true"
              ) && (
                <button
                  onClick={() => {
                    toggleTareaEstado(selectedTarea);
                  }}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold shadow-sm transition-all active:scale-95 bg-brand-500 hover:bg-brand-600 shadow-brand-200 dark:shadow-none"
                >
                  <CheckCircle className="size-4" /> Marcar como Completada
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
